/**
 * StreamView font choices for the "now playing" text (issue #9), loaded from
 * Google Fonts on demand (see `ensureOverlayFontLoaded`) rather than a static
 * `index.html` block — the public overlay page has no reason to pull every
 * family when only one is ever shown at a time.
 *
 * Helvetica, Futura and Times New Roman are classic proprietary typefaces not
 * distributed on Google Fonts — those three are left as plain system-font
 * references (present on most Mac/Windows machines, which is what StreamView
 * actually renders on) with a generic fallback rather than a webfont. Garamond
 * and Bodoni use their standard open-source Google Fonts substitutes (EB
 * Garamond, Bodoni Moda) under the requested display name.
 *
 * Keep `key`s in sync with OVERLAY_FONTS in api/src/handlers/settings.ts.
 */
export interface OverlayFontOption {
  key: string;
  label: string;
  cssFamily: string;
  /** Google Fonts family query param (css2 API). Absent ⇒ system font only. */
  googleFont?: string;
}

export const OVERLAY_FONTS: OverlayFontOption[] = [
  { key: "roboto", label: "Roboto", cssFamily: '"Roboto", system-ui, sans-serif', googleFont: "Roboto:wght@700" },
  { key: "open-sans", label: "Open Sans", cssFamily: '"Open Sans", system-ui, sans-serif', googleFont: "Open+Sans:wght@700" },
  { key: "lato", label: "Lato", cssFamily: '"Lato", system-ui, sans-serif', googleFont: "Lato:wght@700" },
  { key: "montserrat", label: "Montserrat", cssFamily: '"Montserrat", system-ui, sans-serif', googleFont: "Montserrat:wght@700" },
  { key: "helvetica", label: "Helvetica", cssFamily: 'Helvetica, Arial, sans-serif' },
  { key: "garamond", label: "Garamond", cssFamily: '"EB Garamond", Garamond, serif', googleFont: "EB+Garamond:wght@600" },
  { key: "futura", label: "Futura", cssFamily: 'Futura, "Century Gothic", sans-serif' },
  { key: "times-new-roman", label: "Times New Roman", cssFamily: '"Times New Roman", Times, serif' },
  { key: "bodoni", label: "Bodoni", cssFamily: '"Bodoni Moda", "Bodoni MT", serif', googleFont: "Bodoni+Moda:wght@700" },
];

const injected = new Set<string>();

/** Inject the `<link>` for a font's Google Fonts family the first time it's used. */
export function ensureOverlayFontLoaded(key: string): void {
  const font = OVERLAY_FONTS.find((f) => f.key === key);
  if (!font?.googleFont || injected.has(font.key)) return;
  injected.add(font.key);
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${font.googleFont}&display=swap`;
  document.head.appendChild(link);
}

export function overlayFontFamily(key: string): string {
  return OVERLAY_FONTS.find((f) => f.key === key)?.cssFamily ?? OVERLAY_FONTS[0].cssFamily;
}
